import { useAuth } from "@clerk/clerk-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { clearFinishedProcessingJobs, fetchProcessingJobs, retryProductBuildActivity } from "./api";
import {
    isActiveProcessingJob,
    isFailedProcessingJob,
    isFinishedProcessingJob,
    isQueuedProcessingJob,
    isRunningProcessingJob,
    type ProcessingJob
} from "./types";
import { BackgroundActivityContext } from "./useBackgroundActivity";
import { useOptionalBatchGeneration } from "../batchGeneration";
import type { TrackedBatch } from "../batchGeneration/types";

export const BACKGROUND_ACTIVITY_POLL_MS = 5000;
export const BACKGROUND_ACTIVITY_OPEN_STORAGE_PREFIX = "intelligenic.backgroundActivity.open.v1";
export const PRODUCT_BUILD_RETRY_SUCCEEDED_EVENT = "intelligenic:product-build-retry-succeeded";

interface BackgroundActivityProviderProps {
    children: ReactNode;
}

const readOpenState = (key: string): boolean => {
    if (typeof window === "undefined") return false;
    try {
        return window.sessionStorage.getItem(key) === "true";
    } catch {
        return false;
    }
};

const writeOpenState = (key: string, open: boolean): void => {
    if (typeof window === "undefined") return;
    try {
        window.sessionStorage.setItem(key, String(open));
    } catch {
        // Storage is an enhancement; privacy settings must not disable the hub.
    }
};

const errorMessage = (error: unknown, fallback: string): string => (error instanceof Error && error.message ? error.message : fallback);

export function BackgroundActivityProvider({ children }: BackgroundActivityProviderProps) {
    const { getToken, isLoaded, userId } = useAuth();
    const batchGeneration = useOptionalBatchGeneration();
    const getTokenRef = useRef(getToken);
    getTokenRef.current = getToken;

    const [jobs, setJobs] = useState<ProcessingJob[]>([]);
    const [batches, setBatches] = useState<TrackedBatch[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
    const [retryErrors, setRetryErrors] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [focusedJobId, setFocusedJobId] = useState<string | null>(null);
    const inFlightRef = useRef<Promise<void> | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);
    const retryingJobRef = useRef<string | null>(null);

    const storageKey = `${BACKGROUND_ACTIVITY_OPEN_STORAGE_PREFIX}:${userId ?? "signed-out"}`;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        if (!batchGeneration) {
            setBatches([]);
            return;
        }
        return batchGeneration.registry.subscribe(setBatches);
    }, [batchGeneration]);

    useEffect(() => {
        setFocusedJobId(null);
        setIsOpen(userId ? readOpenState(storageKey) : false);
    }, [storageKey, userId]);

    const refresh = useCallback((): Promise<void> => {
        if (inFlightRef.current) return inFlightRef.current;

        const controller = new AbortController();
        abortRef.current = controller;
        const request = (async () => {
            setIsRefreshing(true);
            try {
                const token = await getTokenRef.current();
                if (!token) throw new Error("Your session is unavailable. Please sign in again.");
                const response = await fetchProcessingJobs(token, controller.signal);
                if (!mountedRef.current || controller.signal.aborted) return;
                setJobs(response.jobs);
                setError(null);
            } catch (requestError) {
                if (!mountedRef.current || controller.signal.aborted) return;
                setError(errorMessage(requestError, "Failed to load background activity."));
            } finally {
                if (mountedRef.current && !controller.signal.aborted) {
                    setIsLoading(false);
                    setIsRefreshing(false);
                }
                if (abortRef.current === controller) {
                    abortRef.current = null;
                    inFlightRef.current = null;
                }
            }
        })();
        inFlightRef.current = request;
        return request;
    }, []);

    useEffect(() => {
        if (!isLoaded || !userId) {
            if (isLoaded) {
                setJobs([]);
                setError(null);
                setIsLoading(false);
            }
            return;
        }

        // Never show one account's last response while Clerk switches to
        // another authenticated account in the same tab.
        setJobs([]);
        setError(null);
        setIsLoading(true);
        void refresh();
        const interval = window.setInterval(() => void refresh(), BACKGROUND_ACTIVITY_POLL_MS);
        const onFocus = () => void refresh();
        window.addEventListener("focus", onFocus);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener("focus", onFocus);
            abortRef.current?.abort();
            abortRef.current = null;
            inFlightRef.current = null;
        };
    }, [isLoaded, refresh, userId]);

    const toggle = useCallback(() => {
        setFocusedJobId(null);
        setIsOpen(current => {
            const next = !current;
            writeOpenState(storageKey, next);
            return next;
        });
    }, [storageKey]);

    const close = useCallback(() => {
        setFocusedJobId(null);
        setIsOpen(false);
        writeOpenState(storageKey, false);
    }, [storageKey]);

    const openForJob = useCallback(
        (jobId?: string | null) => {
            setFocusedJobId(jobId || null);
            setIsOpen(true);
            writeOpenState(storageKey, true);
        },
        [storageKey]
    );

    const clearFinished = useCallback(async () => {
        setIsClearing(true);
        try {
            const token = await getTokenRef.current();
            if (!token) throw new Error("Your session is unavailable. Please sign in again.");
            const response = await clearFinishedProcessingJobs(token);
            if (!mountedRef.current) return;
            if (response) {
                setJobs(response.jobs);
                setError(null);
            } else {
                if (inFlightRef.current) await inFlightRef.current;
                await refresh();
            }
            batchGeneration?.registry.clearFinished();
        } catch (clearError) {
            if (mountedRef.current) {
                setError(errorMessage(clearError, "Failed to clear finished activity."));
            }
        } finally {
            if (mountedRef.current) setIsClearing(false);
        }
    }, [batchGeneration, refresh]);

    const retryFailedJob = useCallback(async (jobId: string) => {
        if (retryingJobRef.current) return;
        const job = jobs.find(item => item.id === jobId);
        const failedActivity = job?.kind === "product"
            ? job.activities.find(isFailedProcessingJob)
            : null;
        if (!job || !job.productId || !failedActivity) return;

        retryingJobRef.current = jobId;
        setRetryingJobId(jobId);
        setRetryErrors(current => {
            const next = { ...current };
            delete next[jobId];
            return next;
        });
        try {
            const token = await getTokenRef.current();
            if (!token) throw new Error("Your session is unavailable. Please sign in again.");
            await retryProductBuildActivity(job.productId, failedActivity.type, token);
            if (!mountedRef.current) return;
            window.dispatchEvent(new CustomEvent(PRODUCT_BUILD_RETRY_SUCCEEDED_EVENT, {
                detail: { productId: job.productId }
            }));
            if (inFlightRef.current) await inFlightRef.current;
            await refresh();
        } catch (retryError) {
            if (mountedRef.current) {
                setRetryErrors(current => ({
                    ...current,
                    [jobId]: errorMessage(retryError, "Failed to retry this Product build.")
                }));
            }
        } finally {
            retryingJobRef.current = null;
            if (mountedRef.current) setRetryingJobId(null);
        }
    }, [jobs, refresh]);

    const counts = useMemo(
        () => ({
            activeCount: jobs.filter(isActiveProcessingJob).length,
            runningCount: jobs.filter(isRunningProcessingJob).length,
            queuedCount: jobs.filter(isQueuedProcessingJob).length,
            finishedCount: jobs.filter(isFinishedProcessingJob).length,
            failedCount: jobs.filter(isFailedProcessingJob).length
        }),
        [jobs]
    );

    const value = useMemo(
        () => ({
            jobs,
            batches,
            ...counts,
            isLoading,
            isRefreshing,
            isClearing,
            retryingJobId,
            retryErrors,
            error,
            isOpen,
            focusedJobId,
            toggle,
            close,
            openForJob,
            refresh,
            clearFinished,
            retryFailedJob
        }),
        [jobs, batches, counts, isLoading, isRefreshing, isClearing, retryingJobId, retryErrors, error, isOpen, focusedJobId, toggle, close, openForJob, refresh, clearFinished, retryFailedJob]
    );

    return <BackgroundActivityContext.Provider value={value}>{children}</BackgroundActivityContext.Provider>;
}
